import { useEffect, useMemo, useState } from 'react';
import { authClient } from '@frontend/api-clients/authClient';
import { fetchDashboardHealth } from '@frontend/api-clients/dashboardClient';
import { fetchAdminSystemResources } from '@frontend/api-clients/adminHubClient';
import { getRedirectEntries, getServiceWidgets } from '@frontend/config/serviceLinkRegistry';
import { usePublicIntegration } from '@frontend/context/PublicIntegrationContext';
import type { User } from '@frontend/types';
import type { DashboardHealthCheck, ServerResources, ServiceSummary } from '@frontend/types/adminhub';
import { canSeeSystemAdmin } from '@frontend/utils/adminHubAccess';
import { useAdminWidgetExpanded } from './hooks/useAdminWidgetExpanded';
import SystemPanel from './system/components/SystemPanel';
import ServicesPanel from './system/components/ServicesPanel';

interface SystemAdminPageProps {
  user: User | null;
}

export default function SystemAdminPage({ user }: SystemAdminPageProps) {
  const appOrigin = typeof window === 'undefined' ? '' : window.location.origin;
  const integration = usePublicIntegration();
  const [serviceHealth, setServiceHealth] = useState<DashboardHealthCheck[]>([]);
  const [installedServices, setInstalledServices] = useState<string[] | null>(null);
  const [serverResources, setServerResources] = useState<ServerResources>({
    cpu: { total: 0, used: 0, free: 0, usedPercent: 0 },
    ram: { total: 0, used: 0, free: 0, usedPercent: 0 },
    disk: { total: 0, used: 0, free: 0, usedPercent: 0 },
    containers: [],
  });
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [resourcesError, setResourcesError] = useState('');

  const serviceWidgets = useMemo(() => {
    const fromIntegration = Array.isArray(integration.installedServices)
      ? integration.installedServices
      : [];
    const fromHealth = Array.isArray(installedServices) ? installedServices : [];
    // Union both sources so a stale health list cannot drop opt-in services
    // (e.g. netbird) that public-integration-config already reports.
    const merged = [...new Set([...fromIntegration, ...fromHealth])];
    return getServiceWidgets(appOrigin, {
      ...integration,
      installedServices: merged.length > 0 ? merged : integration.installedServices,
    });
  }, [appOrigin, integration, installedServices]);

  const { expanded, toggleWidget } = useAdminWidgetExpanded(user, 'system', {
    system: true,
    services: false,
    keycloak: false,
    openproject: true,
    gitea: false,
    nextcloud: false,
    woodpecker: false,
    vaultwarden: false,
    coturn: false,
  });

  useEffect(() => {
    if (!canSeeSystemAdmin(user)) return;

    const loadHealth = async () => {
      try {
        const token = authClient.getToken();
        if (!token) return;
        const data = await fetchDashboardHealth(token);
        setServiceHealth(Array.isArray(data.services) ? data.services : []);
        if (Array.isArray(data.installedServices)) {
          setInstalledServices(data.installedServices);
        }
      } catch {
        setServiceHealth([]);
      }
    };

    const loadResources = async () => {
      setResourcesLoading(true);
      setResourcesError('');
      try {
        const token = authClient.getToken();
        if (!token) return;
        const data = await fetchAdminSystemResources(token);
        setServerResources(data);
      } catch {
        setResourcesError('Could not load resource data.');
      } finally {
        setResourcesLoading(false);
      }
    };

    void loadHealth();
    void loadResources();
  }, [user]);

  const summarizeService = (serviceId: string): ServiceSummary => {
    const checks = serviceHealth.filter((item) => item.service === serviceId);
    if (checks.length === 0) {
      // No probe row yet — pending load or transient; do not claim unconfigured when Launch works.
      return {
        status: 'unknown',
        missing: [] as string[],
        url: '',
      };
    }

    if (checks.some((item) => item.status === 'down')) {
      return {
        status: 'down',
        missing: [...new Set(checks.flatMap((item) => item.missing || []))],
        url: checks.find((item) => item.url)?.url || '',
      };
    }

    if (checks.some((item) => item.status === 'up')) {
      return {
        status: 'up',
        missing: [] as string[],
        url: checks.find((item) => item.url)?.url || '',
      };
    }

    if (checks.some((item) => item.status === 'unconfigured')) {
      return {
        status: 'unconfigured',
        missing: [...new Set(checks.flatMap((item) => item.missing || []))],
        url: checks.find((item) => item.url)?.url || '',
      };
    }

    return {
      status: 'unknown',
      missing: [] as string[],
      url: checks.find((item) => item.url)?.url || '',
    };
  };

  const fmtBytes = (bytes: number) => {
    if (bytes >= 1_073_741_824) return `${(bytes / 1_073_741_824).toFixed(1)} GB`;
    if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(0)} MB`;
    return `${(bytes / 1024).toFixed(0)} KB`;
  };

  const meterLevel = (pct: number) => {
    if (pct >= 90) return 'level-crit';
    if (pct >= 70) return 'level-warn';
    return 'level-ok';
  };

  const servicesStatus = (() => {
    const statuses = serviceWidgets.map((s) => summarizeService(s.id).status);
    if (statuses.some((s) => s === 'down')) return 'down';
    if (statuses.some((s) => s === 'up')) return 'up';
    return 'unknown';
  })();

  const redirectEntries = useMemo(() => getRedirectEntries(appOrigin), [appOrigin]);

  if (!canSeeSystemAdmin(user)) {
    return (
      <div className="tree-dashboard-panel">
        <h2>System Admin</h2>
        <p className="tree-dashboard-muted">You do not have permission to view this section.</p>
      </div>
    );
  }

  return (
    <div className="tree-dashboard-panel">
      <h2>System Admin</h2>
      <p className="tree-dashboard-muted">
        Platform host resources, service widgets, and redirect registry. Ecosystem management is under Realm
        Admin.
      </p>

      <SystemPanel
        expanded={Boolean(expanded.system)}
        status={resourcesError ? 'down' : resourcesLoading ? 'unknown' : 'up'}
        redirectEntries={redirectEntries}
        serverResources={serverResources}
        loadingResources={resourcesLoading}
        resourcesError={resourcesError}
        onToggle={() => toggleWidget('system')}
        fmtBytes={fmtBytes}
        meterLevel={meterLevel}
      />

      <ServicesPanel
        expanded={Boolean(expanded.services)}
        servicesStatus={servicesStatus}
        serviceWidgets={serviceWidgets}
        expandedMap={expanded}
        onToggleSection={() => toggleWidget('services')}
        onToggleWidget={toggleWidget}
        summarizeService={summarizeService}
      />
    </div>
  );
}
